/* Copyright (c) 2018 Backpack Health LLC */
import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import GroupTitle from '../../features/GroupTitle/GroupTitle.presenter';
import { ClampedText } from '../../components/ClampedText';
import { GroupThemeMarkdown } from '../../components/Markdown';
import { IconSurvey } from '../../components/icons/SvgIcons';

import { SurveyPageLayout } from './SurveyQuestion/components/SurveyPageLayout';
import { UserTypedMessage } from './components/UserTypedMessage';

import { rgb2css } from '../../../utilities/types';
import RouteNames from '../../appAuthorized/RouteNames';
import { useUIState } from '../../hooks/useUIState';
import { useSurveyForActiveProfile, useSurveysForActiveProfileGroup, useSurveyDefinitionsForActiveProfileGroup } from '../../hooks/useSurvey';
import { useNavigate } from '../../hooks/useNavigate';
import { Card, Spacer, Icons, Buttons } from '../../../bphComponents/bundle';
import COLORS from '../../styles/Colors';
import { InProgressSurvey, NewSurvey } from './components/SurveySummaryBlocks';
import { UserNotUserFormattedMessage } from '../../views/components/UserNotUserFormattedMessage';
import ContentWrapper from '../../components/ContentWrapper';

interface Props {
  match: any;
}

export const SurveySubmitted: React.FC<Props> = (props) => {
  const groupId = Number(props.match.params.groupId);
  const surveyId = Number(props.match.params.surveyId);

  const [, uiActions] = useUIState();
  const [, navActions] = useNavigate();
  const [surveyData] = useSurveyForActiveProfile(surveyId);
  const [{ definitionsLookup }, surveyActions] = useSurveysForActiveProfileGroup(groupId);

  const { definition } = surveyData;
  const surveysByStatus = surveyActions.getGroupSurveysByStatus();
  const hasUnfinishedSurveys = surveysByStatus.inProgress.length > 0;

  React.useEffect(() => {
    uiActions.setActiveGroup(groupId);
  }, []);

  if (!definition) return null;

  return (
    <SurveyPageLayout
      className=""
      navButtons={null}
      groupId={groupId}
      title={<div style={{ paddingTop: 6 }}><FormattedMessage id="surveyCompleted.title.text"/></div>}
      mainTitle={(
        <div style={{ paddingTop: 24 }}>
          <div className="font-caption-1 theme-group base color">
            <GroupTitle
              groupId={groupId}
              size="48"
            />
          </div>
          <div className="theme-group base color font-title-2">
            <FormattedMessage id="surveyCompleted.title.text"/>
          </div>
        </div>
      )}
      extendedHeader={() => (
        <GroupThemeMarkdown text={definition.conclusion_message} />
      )}
    >
      <ContentWrapper>
        {
          hasUnfinishedSurveys ? (
            <>
            <Card.ExternalCardTitle>
              <UserNotUserFormattedMessage
                notUser={{ id: 'surveySubmitted.otherSurveys.notUser.message' }}
                user={{ id: 'surveySubmitted.otherSurveys.user.message' }}
                notUserNameKeys={null}
                userNameKeys={null}
              />
            </Card.ExternalCardTitle>
            {
              surveysByStatus.inProgress.map(s => (
                s.completion_percentage
                ? <InProgressSurvey key={s.id} groupId={groupId} survey={s} definition={definitionsLookup[s.survey_definition_id]}/>
                : <NewSurvey key={s.id} groupId={groupId} survey={s} definition={definitionsLookup[s.survey_definition_id]}/>
              ))
            }
            </>
          ) : <Spacer.Height8 />
        }

        <Card.CardShape>
          <Card.CardContent>
            <div style={{ display: 'flex', marginBottom: 10 }}>
              <IconSurvey strokeClassName="stroke-color-gsMedDark"/>

              <div style={{ marginLeft: 10, marginRight: 10 }}>
                <Icons.IconArrowRight.Large color={COLORS.gsMedDark} />
              </div>

              <Icons.IconHealthInfo.Large color={COLORS.gsMedDark} />
            </div>

            <div className="font-color-gsMedDark font-footnote">
              <UserTypedMessage
                user="surveySubmitted.heDataAdded.user.message"
                notUser="surveySubmitted.heDataAdded.notUser.message"
              />
            </div>

            <Spacer.Height16 />

            <Buttons.StandardButtonVariants.SecondaryButton
              className="font-color-profile-accent"
              type={null}
              size="wide"
              textAlign="left"
              onClick={() => navActions.goTo(RouteNames.myBackpack())}
            >
              <FormattedMessage id="surveySubmitted.heDataAdded.goTo.button.label" />
            </Buttons.StandardButtonVariants.SecondaryButton>
          </Card.CardContent>
        </Card.CardShape>
      </ContentWrapper>
    </SurveyPageLayout>
  );
};
